import React, { useState } from 'react'
import {
  List,
  Datagrid,
  TextField,
  Filter,
  TextInput,
  ReferenceInput,
  SelectInput,
  ReferenceArrayInput,
  SelectArrayInput,
  Pagination,
  FunctionField,
  useDataProvider,
} from 'react-admin'
import { makeStyles } from '@material-ui/core/styles'
import IconButton from '@material-ui/core/IconButton'
import Tooltip from '@material-ui/core/Tooltip'
import DesktopWindowsIcon from '@material-ui/icons/DesktopWindows'
import FolderIcon from '@material-ui/icons/Folder'

import FileBrowserModal from '../components/FileBrowserModal'
import { VncEnabledContext, VncEnabledProvider } from './VncEnabledProvider'

const useStyles = makeStyles({
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  textInput: {
    width: 200,
  },
  listWrapper: {
    paddingLeft: '24px !important',  // Consistent left and right padding
  },
})

// Filter with tag support (read-only - techs can filter but not change tags)
const RadTechFilter = props => {
  const classes = useStyles()
  const dataProvider = useDataProvider()
  const [tagTypes, setTagTypes] = React.useState([])

  React.useEffect(() => {
    const fetchTagTypes = async () => {
      try {
        const { data } = await dataProvider.getList('tag-types', {
          pagination: { page: 1, perPage: 1000 },
          sort: { field: 'name', order: 'ASC' },
        })
        setTagTypes(data || [])
      } catch (err) {
        console.error('Error fetching tag types:', err)
      }
    }

    fetchTagTypes()
  }, [dataProvider])

  return (
    <Filter {...props} key={`filter-${tagTypes.length}`}>
      <TextInput
        label='Search'
        source='q'
        alwaysOn
        className={classes.textInput}
      />
      <ReferenceInput
        label='Device Type'
        source='type'
        reference='modalities/types'
        allowEmpty
      >
        <SelectInput optionText='name' />
      </ReferenceInput>
      {tagTypes.map((tagType) => (
        <ReferenceArrayInput
          key={tagType.id}
          source={`tag_${tagType.name}`}
          reference="tags"
          label={`Tag: ${tagType.name}`}
          filter={{ tagTypeId: tagType.id }}
          alwaysOn={false}
          style={{ minWidth: '150px' }}
        >
          <SelectArrayInput optionText="name" />
        </ReferenceArrayInput>
      ))}
    </Filter>
  )
}

// VNC Remote View button
const RemoteScanButton = ({ record }) => {
  const { vncEnabledIds } = React.useContext(VncEnabledContext)
  const [loading, setLoading] = React.useState(false)

  // Only show button if this modality has VNC configured
  if (!record?.id || !vncEnabledIds.includes(record.id)) return null

  const handleClick = async (e) => {
    e.stopPropagation()
    
    if (loading) return
    setLoading(true)

    try {
      // Get auth headers from session (uses AWS Amplify)
      const { getAuthHeaders } = await import('../lib/session')
      const authHeaders = await getAuthHeaders()
      
      // Use direct fetch instead of dataProvider to avoid response transformation
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001'
      const response = await fetch(`${apiUrl}/vnc/sessions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders
        },
        body: JSON.stringify({ modalityId: record.id })
      })

      const responseText = await response.text()
      console.log('VNC session raw response:', responseText)
      
      if (!response.ok) {
        let errorMessage = `HTTP error ${response.status}`
        try {
          const errorData = JSON.parse(responseText)
          errorMessage = errorData.message || errorMessage
        } catch (e) {}
        throw new Error(errorMessage)
      }

      const sessionData = JSON.parse(responseText)
      console.log('VNC session parsed response:', sessionData)

      // Handle potential nested data structure
      const data = sessionData.data || sessionData
      const sessionToken = data.sessionToken
      const wssUrl = data.wssUrl
      
      console.log('Extracted - sessionToken:', sessionToken, 'wssUrl:', wssUrl)

      if (!sessionToken) {
        throw new Error('No session token returned from API')
      }

      // Open VNC viewer in a new window with the session token
      const width = 1280
      const height = 800
      const left = (window.screen.width - width) / 2
      const top = (window.screen.height - height) / 2
      
      // Pass session info via URL params - include wssUrl for the viewer to use
      const viewerUrl = `/vnc-viewer.html?token=${sessionToken}&modalityId=${record.id}&wssUrl=${encodeURIComponent(wssUrl)}`
      
      window.open(
        viewerUrl,
        `vnc-${record.id}`,
        `width=${width},height=${height},left=${left},top=${top},menubar=no,toolbar=no,location=no,status=no,resizable=yes,scrollbars=no`
      )
    } catch (error) {
      console.error('Failed to create VNC session:', error)
      alert(error.message || 'Failed to start VNC session. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Tooltip title="Remote View">
      <IconButton 
        color='primary' 
        onClick={handleClick}
        disabled={loading}
      >
        <DesktopWindowsIcon />
      </IconButton>
    </Tooltip>
  )
}

// File browser button
const FileBrowserButton = ({ record }) => {
  const { fileTransferEnabledIds } = React.useContext(VncEnabledContext)
  const [fileBrowserOpen, setFileBrowserOpen] = useState(false)

  // Only show button if this modality has file transfer enabled
  if (!record?.id || !fileTransferEnabledIds.includes(record.id)) return null

  const handleClick = (e) => {
    e.stopPropagation()
    setFileBrowserOpen(true)
  }

  const handleClose = () => {
    setFileBrowserOpen(false)
  }

  return (
    <>
      <Tooltip title="Browse Files">
        <IconButton 
          color='primary' 
          onClick={handleClick}
        >
          <FolderIcon />
        </IconButton>
      </Tooltip>
      <FileBrowserModal
        open={fileBrowserOpen}
        onClose={handleClose}
        modalityId={record.id}
        modalityName={record.siteName || record.displayName}
        initialPath="C:"
      />
    </>
  )
}

// Simplified actions - VNC and File Browser only
const Actions = (record) => {
  const classes = useStyles()
  return (
    <div className={classes.actions}>
      {record && <RemoteScanButton record={record} />}
      {record && <FileBrowserButton record={record} />}
    </div>
  )
}

// Main list component for RAD_TECH users
const RadTechModalityList = props => {
  const classes = useStyles()
  
  return (
    <div className={classes.listWrapper}>
      <VncEnabledProvider>
        <List
          filters={<RadTechFilter />}
          filterDefaultValues={{ active: 1, metrics: 0 }}  // metrics: 0 skips InfluxDB queries
          bulkActionButtons={false}
          title='Modalities'
          sort={{ field: 'siteName', order: 'ASC' }}
          perPage={50}
          pagination={<Pagination rowsPerPageOptions={[10, 25, 50, 100]} />}
          {...props}
        >
          <Datagrid size='small'>
            <TextField source='siteName' label='Site' />
            <TextField source='serialNumber' label='Serial #' />
            <TextField source='manufacturer' />
            <TextField source='modality' label='Type' />
            <TextField source='model' />
            <FunctionField
              label='Actions'
              render={props => <Actions {...props} />}
            />
          </Datagrid>
        </List>
      </VncEnabledProvider>
    </div>
  )
}

export default RadTechModalityList

